import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Observable, of, ReplaySubject, throwError } from 'rxjs'
import { catchError, map, timeout } from 'rxjs/operators'

import { environment } from '../../../environments/environment'
import { Role } from '../enums/role.enum'
import { User } from '../interfaces/user.interface'

const AUTH_ME_TIMEOUT = 10000
const CACHED_USER_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
const CACHED_USER_KEY = 'cachedUser'
const CACHED_USER_TIMESTAMP_KEY = 'cachedUserTimestamp'

@Injectable()
export class AuthService {
  token: string
  role: string
  baseUrl = environment.apiBaseUrl + 'auth/'

  public currentUser = new ReplaySubject(1)

  constructor(private http: HttpClient, private router: Router) {
    // Set token if saved in local storage
    this.token = localStorage.getItem('token')
    this.role = localStorage.getItem('role')
  }

  login(
    email: string,
    password: string
  ): Observable<{ role: string; userId: number } | boolean> {
    return this.http.post(this.baseUrl + 'login', { email, password }).pipe(
      map(
        (res: { accessToken: string; role: string; userId: number }) => {
          const token = res && res.accessToken
          const role = res && res.role
          this.token = token
          this.role = role
          // Store JWT token and Role in local storage
          localStorage.setItem('token', token)
          localStorage.setItem('role', role)
          localStorage.setItem(CACHED_USER_TIMESTAMP_KEY, Date.now().toString())

          return { role: this.role, userId: res.userId }
        },
        err => err.error.message
      )
    )
  }

  logout(): void {
    this.token = null
    localStorage.removeItem('token')
    localStorage.removeItem('role')
    localStorage.removeItem(CACHED_USER_KEY)
    localStorage.removeItem(CACHED_USER_TIMESTAMP_KEY)
    this.currentUser.next(null)
  }

  check(): boolean {
    return !!this.getToken()
  }

  getToken(): string {
    return localStorage.getItem('token')
  }

  getRole(): Role {
    if (environment.nativescriptEmbedded) {
      // Embedded app considers everyone as simple User.
      return Role.User
    }
    return Role[localStorage.getItem('role')] as Role
  }

  me(): Observable<User | boolean> {
    return this.http.get(this.baseUrl + 'me').pipe(
      timeout(AUTH_ME_TIMEOUT),
      map((res: User) => res)
    )
  }

  private cacheUser(user: User): void {
    localStorage.setItem(CACHED_USER_KEY, JSON.stringify(user))
    localStorage.setItem(CACHED_USER_TIMESTAMP_KEY, Date.now().toString())
  }

  getCachedUser(): User | null {
    const raw = localStorage.getItem(CACHED_USER_KEY)
    const ts = localStorage.getItem(CACHED_USER_TIMESTAMP_KEY)
    if (!raw || !ts) {
      return null
    }
    if (Date.now() - Number(ts) > CACHED_USER_MAX_AGE_MS) {
      return null
    }
    try {
      return JSON.parse(raw) as User
    } catch {
      return null
    }
  }

  meWithCache(): Observable<User> {
    return this.http.get(this.baseUrl + 'me').pipe(
      timeout(AUTH_ME_TIMEOUT),
      map((res: User) => {
        this.cacheUser(res)
        return res
      }),
      catchError(err => {
        const cached = this.getCachedUser()
        if (cached) {
          return of(cached)
        }
        return throwError(err)
      })
    )
  }

  revalidate(): Observable<User> {
    return this.http.get(this.baseUrl + 'me').pipe(
      timeout(AUTH_ME_TIMEOUT),
      map((res: User) => {
        this.cacheUser(res)
        return res
      })
    )
  }

  async getFullMe(): Promise<User> {
    let me = await this.me().toPromise() as User;
    return this.http.get(environment.apiBaseUrl + 'users/' + me.id).pipe(map((res: User) => res)).toPromise();
  }

  sendResetPasswordEmail(email: string) {
    let params = new HttpParams()
    params = params.set('email', email)

    return this.http
      .get(this.baseUrl + 'forgot-password', { params })
      .pipe(map((res: any) => res))
  }

  resetPassword(newPassword: string, token: string): Observable<any> {
    const formData = new FormData()
    formData.append('newPassword', newPassword)
    formData.append('token', token)

    return this.http
      .post(this.baseUrl + 'reset-password', formData)
      .pipe(map((res: any) => res))
  }
}
